import React, { useRef, useState } from 'react';
import { bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { ARRAY_ERROR } from 'final-form';
import { Form as FinalForm, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { isUploadImageOverLimitError } from '../../util/errors';
import { useConfirmationModal } from '../../util/hooks';

// Import shared components
import {
  Form,
  AspectRatioWrapper,
  NamedLink,
  PrimaryButtonInline,
  PreviewListingButton,
  Icon,
} from '../../components';

// Import modules from this directory
import UploadButton from './UploadButton';
import ListingVideo from './ListingVideo';
import css from './EditLiveRoomBehindTheScenesForm.module.css';

const ACCEPT_VIDEOS = 'video/*';

const VideoUploadError = props => {
  return props.uploadOverLimit ? (
    <p className={css.error}>
      <FormattedMessage id="EditLiveRoomBehindTheScenesForm.videoUploadFailed.uploadOverLimit" />
    </p>
  ) : props.uploadVideoError ? (
    <p className={css.error}>
      <FormattedMessage id="EditLiveRoomBehindTheScenesForm.videoUploadFailed.uploadFailed" />
    </p>
  ) : null;
};

// NOTE: PublishListingError and showListingError are here since Photos panel is the last visible panel
// before creating a new listing. If that order is changed, these should be changed too.
// Create and show listing errors are shown above submit button
const PublishListingError = props => {
  return props.error ? (
    <p className={css.error}>
      <FormattedMessage id="EditLiveRoomBehindTheScenesForm.publishListingFailed" />
    </p>
  ) : null;
};

const ShowListingError = props => {
  return props.error ? (
    <p className={css.error}>
      <FormattedMessage id="EditLiveRoomBehindTheScenesForm.showListingFailed" />
    </p>
  ) : null;
};

// Field component that uses file-input to allow user to select images or videos.
export const FieldAddFile = props => {
  const {
    formApi,
    onInitiateUploads,
    aspectWidth = 1,
    aspectHeight = 1,
    inputRef,
    ...rest
  } = props;
  return (
    <Field form={null} {...rest}>
      {fieldprops => {
        const { accept, input, label, disabled: fieldDisabled } = fieldprops;
        const { name, type } = input;
        const onChange = e => {
          const files = Array.from(e.target.files);

          if (files.length === 0) return;

          // Create file objects with temp IDs
          const fileObjects = files.map(file => ({
            id: `temp-${file.name}-${Date.now()}-${Math.random()}`,
            file: file,
          }));

          // Initiate all uploads immediately - this will show all files in UI
          // The Redux actions handle sequential vs parallel uploads internally
          if (onInitiateUploads) {
            onInitiateUploads(fileObjects);
          }

          // Clear the input so the same files can be selected again
          e.target.value = '';
        };
        const inputProps = {
          accept,
          id: name,
          name,
          onChange,
          type,
          ref: inputRef,
          multiple: true,
        };
        return (
          <div className={css.addFileWrapper}>
            <AspectRatioWrapper width={aspectWidth} height={aspectHeight}>
              {fieldDisabled ? null : <input {...inputProps} className={css.addFileInput} />}
              <label htmlFor={name} className={css.addFile}>
                {label}
              </label>
            </AspectRatioWrapper>
          </div>
        );
      }}
    </Field>
  );
};

// Component that shows listing videos from "vidos" field array
const FieldListingVideo = props => {
  const {
    name,
    controlMenuOpen,
    onToggleControlMenu,
    onRemoveVideo,
    aspectWidth,
    aspectHeight,
  } = props;

  return (
    <Field name={name}>
      {fieldProps => {
        const { input } = fieldProps;
        const video = input.value;

        const videoId = video.id || video.videoId;
        const isMenuOpen = controlMenuOpen === videoId;

        return video ? (
          <ListingVideo
            video={video}
            key={video.videoId || video.id}
            className={css.thumbnail}
            isMenuOpen={isMenuOpen}
            onToggleMenu={menuOpen => {
              // If menuOpen is truthy (video object), extract the ID and set it
              // If menuOpen is falsy (null), set controlMenuOpen to null
              const videoIdToOpen = menuOpen ? menuOpen.id || menuOpen.videoId : null;
              onToggleControlMenu(videoIdToOpen);
            }}
            onRemoveVideo={() => onRemoveVideo(video?.videoId || video?.id)}
            aspectWidth={aspectWidth}
            aspectHeight={aspectHeight}
          />
        ) : null;
      }}
    </Field>
  );
};

export const EditLiveRoomBehindTheScenesFormComponent = props => {
  const videoRef = useRef(null);

  const [state, setState] = useState({ videoUploadRequested: false });
  const [submittedVideos, setSubmittedVideos] = useState([]);
  const [controlMenuVideoOpen, toggleControlMenuVideoOpen] = useState(null);

  const { open: onOpenConfirmationModal, modalElement } = useConfirmationModal();

  const initiateVideoUploads = fileObjects => {
    const { onVideoUpload } = props;

    setState(prev => ({ ...prev, videoUploadRequested: true }));

    // Start all uploads immediately - each onVideoUpload call will:
    // 1. Immediately dispatch REQUEST action (shows file in UI with spinner)
    // 2. Do the actual upload in parallel
    // 3. Dispatch SUCCESS action (removes spinner) when done
    const uploadPromises = fileObjects.map(async fileObj => {
      try {
        await onVideoUpload({ id: fileObj.id, file: fileObj.file });
      } catch (error) {
        console.error('Video upload failed:', error);
      }
    });

    // Wait for all uploads to complete, then reset state
    Promise.all(uploadPromises).then(() => {
      setState(prev => ({ ...prev, videoUploadRequested: false }));
    });
  };

  const openFileDialog = fileInputRef => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      render={formRenderProps => {
        const {
          form,
          className,
          fetchErrors,
          handleSubmit,
          intl,
          invalid,
          onRemoveVideo,
          onGetPrevPathParams,
          disabled,
          ready,
          saveActionMsg,
          updated,
          updateInProgress,
          touched,
          errors,
          values,
          listing,
          panelTitle,
        } = formRenderProps;

        const videos = values.videos || [];

        const prevPathParams = onGetPrevPathParams();

        const { publishListingError, showListingError, updateListingError, uploadVideoError } =
          fetchErrors || {};
        const uploadVideoOverLimit = isUploadImageOverLimitError(uploadVideoError);

        const arrayOfVideoIds = videos => videos.map(v => v.id || v.videoId);
        const videoIdsFromProps = arrayOfVideoIds(videos);
        const videoIdsFromPreviousSubmit = arrayOfVideoIds(submittedVideos);
        const videoArrayHasSameVideos = isEqual(videoIdsFromProps, videoIdsFromPreviousSubmit);

        const hasVideos = videos.length > 0;
        const submittedOnce = submittedVideos.length > 0;

        // Check if videos were deleted (had videos before, now empty)
        const videosWereDeleted = submittedVideos.length > 0 && videos.length === 0;

        const pristineSinceLastSubmit = submittedOnce && videoArrayHasSameVideos;

        const submitReady =
          (updated && pristineSinceLastSubmit) ||
          ready ||
          (updated && videosWereDeleted) ||
          (updated && !hasVideos && !submittedOnce); // Handle case when no videos and successfully saved
        const submitInProgress = updateInProgress;
        const submitDisabled =
          invalid || disabled || submitInProgress || state.videoUploadRequested || ready;

        const videosError = touched.videos && errors?.videos && errors.videos[ARRAY_ERROR];

        const getAspectWidthForVideoUpload = hasFiles => (hasFiles ? 5 : 6);
        const getAspectHeightForVideoUpload = hasFiles => (hasFiles ? 7 : 4);

        const aspectWidthForVideoUpload = getAspectWidthForVideoUpload(hasVideos);
        const aspectHeightForVideoUpload = getAspectHeightForVideoUpload(hasVideos);

        const addVideoLabel = hasVideos ? (
          <span className={classNames(css.chooseFileText, css.chooseFileTextWithFiles)}>
            <Icon name="video" size={48} className={css.chooseFileIcon} />
            <span className={css.chooseFile}>
              <FormattedMessage id="EditLiveRoomBehindTheScenesForm.chooseVideo" />
            </span>
            <span className={css.fileTypes}>
              <FormattedMessage id="EditLiveRoomBehindTheScenesForm.videoTypes" />
            </span>
          </span>
        ) : (
          <span className={css.chooseFileText}>
            <Icon name="video" size={48} className={css.chooseFileIcon} />
            <span className={css.chooseFile}>
              <FormattedMessage id="EditLiveRoomBehindTheScenesForm.chooseVideo" />
            </span>
            <span className={css.fileTypes}>
              <FormattedMessage id="EditLiveRoomBehindTheScenesForm.videoTypes" />
            </span>
          </span>
        );

        const videoTooltipItems = [
          intl.formatMessage({ id: 'EditLiveRoomBehindTheScenesForm.uploadingVideosHelper1' }),
          intl.formatMessage({ id: 'EditLiveRoomBehindTheScenesForm.uploadingVideosHelper2' }),
          intl.formatMessage({ id: 'EditLiveRoomBehindTheScenesForm.uploadingVideosHelper3' }),
        ];

        const classes = classNames(css.root, className);
        const videosFileClasses = classNames(css.filesField, {
          [css.filesFieldArray]: hasVideos,
        });

        return (
          <Form
            className={classes}
            onSubmit={e => {
              setSubmittedVideos(videos);
              handleSubmit(e);
            }}
          >
            <div className={css.content}>
              <div className={css.contentWrapper}>
                <PublishListingError error={publishListingError} />
                <ShowListingError error={showListingError} />

                {updateListingError ? (
                  <p className={css.error}>
                    <FormattedMessage id="EditLiveRoomGalleryForm.updateFailed" />
                  </p>
                ) : null}

                <h1 className={css.title}>{panelTitle}</h1>
                <p className={css.subTitle}>
                  <FormattedMessage id="EditLiveRoomBehindTheScenesForm.videosSubTitle" />
                </p>

                <UploadButton
                  titleMsgId={'EditLiveRoomBehindTheScenesForm.uploadVideosTitle'}
                  tooltipItems={videoTooltipItems}
                  disabled={submitDisabled}
                  onClick={() => openFileDialog(videoRef)}
                />

                <div className={videosFileClasses}>
                  <FieldArray name="videos">
                    {({ fields }) =>
                      fields.map((name, index) => (
                        <FieldListingVideo
                          key={name}
                          name={name}
                          controlMenuOpen={controlMenuVideoOpen}
                          onToggleControlMenu={toggleControlMenuVideoOpen}
                          onRemoveVideo={async videoId => {
                            const confirm = await onOpenConfirmationModal({
                              id: 'EditLiveRoomBehindTheScenesForm.confirmRemoveVideo',
                              title: intl.formatMessage({
                                id: 'EditLiveRoomBehindTheScenesForm.confirmRemoveVideoTitle',
                              }),
                              description: intl.formatMessage({
                                id: 'EditLiveRoomBehindTheScenesForm.confirmRemoveVideoDescription',
                              }),
                            });

                            if (confirm) {
                              // Close the control menu if this video's menu was open
                              if (controlMenuVideoOpen === videoId) {
                                toggleControlMenuVideoOpen(null);
                              }
                              fields.remove(index);
                              onRemoveVideo(videoId);
                            }
                          }}
                          aspectWidth={aspectWidthForVideoUpload}
                          aspectHeight={aspectHeightForVideoUpload}
                        />
                      ))
                    }
                  </FieldArray>

                  <FieldAddFile
                    id="addVideo"
                    name="addVideo"
                    accept={ACCEPT_VIDEOS}
                    label={addVideoLabel}
                    type="file"
                    disabled={state.videoUploadRequested}
                    formApi={form}
                    inputRef={videoRef}
                    aspectWidth={aspectWidthForVideoUpload}
                    aspectHeight={aspectHeightForVideoUpload}
                    onInitiateUploads={initiateVideoUploads}
                  />
                </div>

                {videosError ? <div className={css.arrayError}>{videosError}</div> : null}

                <VideoUploadError
                  uploadOverLimit={uploadVideoOverLimit}
                  uploadImageError={uploadVideoError}
                />
              </div>
            </div>

            <div className={css.submitButtonRoot}>
              <NamedLink className={css.backButton} name="EditLiveRoomPage" params={prevPathParams}>
                <FormattedMessage id="EditLiveRoomBehindTheScenesForm.backButton" />
              </NamedLink>
              <div className={css.submitButtonRootFlex}>
                <PreviewListingButton listing={listing} />
                <PrimaryButtonInline
                  size="small"
                  checkmarkSize="small"
                  type="submit"
                  inProgress={submitInProgress}
                  disabled={submitDisabled}
                  ready={submitReady}
                >
                  {saveActionMsg}
                </PrimaryButtonInline>
              </div>
            </div>
            {modalElement}
          </Form>
        );
      }}
    />
  );
};

EditLiveRoomBehindTheScenesFormComponent.defaultProps = { fetchErrors: null };

EditLiveRoomBehindTheScenesFormComponent.propTypes = {
  fetchErrors: shape({
    publishListingError: propTypes.error,
    showListingError: propTypes.error,
    uploadImageError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  onRemoveVideo: func.isRequired,

  // from withRouter

  location: shape({
    search: string.isRequired,
  }).isRequired,
};

export default compose(injectIntl, withRouter)(EditLiveRoomBehindTheScenesFormComponent);
